import { fetchFromRTM } from './index';
import * as constants from './constants';
import './typedef';

class ComponentResolver {
  constructor(connection) {
    this.connection = connection;
  }

  /**
   * Creates a component for an interactive. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-interactive-component-create/)
   * @param {string} interactiveId
   * @param {string} pluginId
   * @param {string} pluginVersion
   * @param {string=} config
   * @returns {Promise.<ComponentDTO>}
   */
  authorInteractiveComponentCreate = async (interactiveId, pluginId, pluginVersion, config) => {
    const message = {
      type: constants.RTM_AUTHOR_INTERACTIVE_COMPONENT_CREATE,
      interactiveId,
      pluginId,
      pluginVersion,
      config,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_INTERACTIVE_COMPONENT_CREATE_OK,
      constants.RTM_AUTHOR_INTERACTIVE_COMPONENT_CREATE_ERROR,
    );

    return response.component;
  };

  /**
   * Creates a component for an activity. See
   *   [RTM Docs](https://dev-developers.smartsparrow.com/messages/author-activity-component-create/)
   * @param {string} activityId
   * @param {string} pluginId
   * @param {string} pluginVersion
   * @param {string=} config
   * @returns {Promise.<ComponentDTO>}
   */
  authorActivityComponentCreate = async (activityId, pluginId, pluginVersion, config) => {
    const message = {
      type: constants.RTM_AUTHOR_ACTIVITY_COMPONENT_CREATE,
      activityId,
      pluginId,
      pluginVersion,
      config,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ACTIVITY_COMPONENT_CREATE_OK,
      constants.RTM_AUTHOR_ACTIVITY_COMPONENT_CREATE_ERROR,
    );

    return response.component;
  };

  /**
   * Delete an interactive component. See
   *   [RTM Documentation](https://dev-developers.smartsparrow.com/messages/author-interactive-component-delete/)
   * @param {string} componentId
   * @param {string} interactiveId
   * @returns {Promise.<{componentId: string, interactiveId: string}}
   */
  authorInteractiveComponentDelete = async (componentId, interactiveId) => {
    const message = {
      type: constants.RTM_AUTHOR_INTERACTIVE_COMPONENT_DELETE,
      componentId,
      interactiveId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_INTERACTIVE_COMPONENT_DELETE_OK,
      constants.RTM_AUTHOR_INTERACTIVE_COMPONENT_DELETE_ERROR,
    );
    return response;
  };

  /**
   * Delete multiple interactive components. See
   *   [RTM Documentation](https://unsightly-rock.cloudvent.net/messages/author-interactive-components-delete/).
   * @param {[string]} componentIds
   * @param {string} interactiveId
   * @returns {Promise.<{componentIds: [string], interactiveId: string}}
   */
  authorInteractiveComponentsDelete = async (componentIds, interactiveId) => {
    const message = {
      type: constants.RTM_AUTHOR_INTERACTIVE_COMPONENTS_DELETE,
      componentIds,
      interactiveId,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_INTERACTIVE_COMPONENTS_DELETE_OK,
      constants.RTM_AUTHOR_INTERACTIVE_COMPONENTS_DELETE_ERROR,
    );
    return response;
  };

  /**
   * Restores components for an interactive. See
   *   [RTM Documentation](https://unsightly-rock.cloudvent.net/messages/author-interactive-component-restore/)
   * @param {string} interactiveId
   * @param {[string]} componentIds
   * @returns {Promise.<[ComponentSummaryDto]>}
   */
    authorInteractiveComponentRestore = async (interactiveId, componentIds) => {
      const message = {
        type: constants.RTM_AUTHOR_INTERACTIVE_COMPONENT_RESTORE,
        interactiveId,
        componentIds,
      };
  
      const response = await fetchFromRTM(
        this.connection,
        message,
        constants.RTM_AUTHOR_INTERACTIVE_COMPONENT_RESTORE_OK,
        constants.RTM_AUTHOR_INTERACTIVE_COMPONENT_RESTORE_ERROR,
      );
      return response;
    };

  /**
   * Delete an activity component. See
   *   [RTM Documentation](https://dev-developers.smartsparrow.com/messages/author-activity-component-delete/)
   * @param {string} componentId
   * @param {string} activityId
   * @returns {Promise.<{componentId: string, activityId: string}}
   */
  authorActivityComponentDelete = async (componentId, activityId) => {
    const message = {
      type: constants.RTM_AUTHOR_ACTIVITY_COMPONENT_DELETE,
      componentId,
      activityId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ACTIVITY_COMPONENT_DELETE_OK,
      constants.RTM_AUTHOR_ACTIVITY_COMPONENT_DELETE_ERROR,
    );
    return response;
  };

  /**
   * Fetches a component from the RTM. See
   *   [RTM Documentation](https://dev-developers.smartsparrow.com/messages/author-component-get/)
   * @param {string} componentId
   * @returns {Promise.<ComponentDTO>}
   */
  authorComponentGet = async (componentId) => {
    const message = {
      type: constants.RTM_AUTHOR_COMPONENT_GET,
      componentId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_COMPONENT_GET_OK,
      constants.RTM_AUTHOR_COMPONENT_GET_ERROR,
    );

    return response.component;
  };

  /**
   * Replaces a components config. See
   *   [RTM Documentation](https://dev-developers.smartsparrow.com/messages/author-component-replace/)
   * @param {string} componentId
   * @param {string=} config as JSON
   * @returns {Promise.<{componentId: string, id: string, config: string}}
   */
  authorComponentConfigReplace = async (componentId, config) => {
    const message = {
      type: constants.RTM_AUTHOR_COMPONENT_CONFIG_REPLACE,
      componentId,
      config,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_COMPONENT_CONFIG_REPLACE_OK,
      constants.RTM_AUTHOR_COMPONENT_CONFIG_REPLACE_ERROR,
    );
    return response.componentConfig;
  };
}

export default ComponentResolver;
